/**
 * Listing page utils: build listing title and other helpers.
 * @typedef {import('../../models/Product').Product} Product
 */

/**
 * Stored locations used to compute default price and available quantity in the listing preview.
 * Only product_suppliers with stored_location in this set are included (e.g. 'Home', 'Meli').
 */
export const STORED_LOCATIONS_FOR_LISTING_DEFAULTS = ['Home', 'Meli'];

/**
 * Default price and available quantity for the listing preview, derived from product_suppliers.
 *
 * We only consider rows that meet both of these conditions:
 * 1. **Location**: stored_location is in the allowed list (e.g. Home, Meli). This limits defaults
 *    to stock that is at home or at Mercado Livre, not other warehouses.
 * 2. **Active and shared stock**: active === true and shared_stock === true. Only links that are
 *    active and participate in shared stock are used, so the listing defaults reflect sellable
 *    inventory that can be fulfilled from the shared pool.
 *
 * From that filtered set: default price is the maximum default_sale_price; default available
 * quantity is the sum of available_amount.
 *
 * @param {Array<{ stored_location?: string | null, default_sale_price?: number | null, available_amount?: number | null, active?: boolean, shared_stock?: boolean }>} productSuppliers
 * @param {string[]} [allowedLocations]
 * @returns {{ defaultPrice: number | null, defaultAvailableQuantity: number }}
 */
export function getListingDefaultsFromProductSuppliers(productSuppliers, allowedLocations = STORED_LOCATIONS_FOR_LISTING_DEFAULTS) {
  const set = new Set(allowedLocations.map((s) => (s || '').trim()).filter(Boolean));
  const filtered = (productSuppliers || []).filter((ps) => {
    const locationOk = ps.stored_location != null && set.has(String(ps.stored_location).trim());
    const activeAndShared = ps.active === true && ps.shared_stock === true;
    return locationOk && activeAndShared;
  });
  let defaultPrice = null;
  let defaultAvailableQuantity = 0;
  for (const ps of filtered) {
    const p = ps.default_sale_price != null ? Number(ps.default_sale_price) : null;
    if (p != null && (defaultPrice === null || p > defaultPrice)) defaultPrice = p;
    const q = ps.available_amount != null ? Number(ps.available_amount) : 0;
    if (Number.isFinite(q)) defaultAvailableQuantity += q;
  }
  return { defaultPrice, defaultAvailableQuantity };
}

/**
 * Same filter as getListingDefaultsFromProductSuppliers: stored_location in allowed list, active === true, shared_stock === true.
 * Use this to get the list of product_suppliers shown in "Pick listing suppliers" and used for publish.
 *
 * @param {Array<{ stored_location?: string | null, active?: boolean, shared_stock?: boolean, [key: string]: unknown }>} productSuppliers
 * @param {string[]} [allowedLocations]
 * @returns {Array<object>} Filtered array of product_supplier rows (same objects, not copies).
 */
export function getEligibleProductSuppliersForListing(productSuppliers, allowedLocations = STORED_LOCATIONS_FOR_LISTING_DEFAULTS) {
  const set = new Set(allowedLocations.map((s) => (s || '').trim()).filter(Boolean));
  return (productSuppliers || []).filter((ps) => {
    const locationOk = ps.stored_location != null && set.has(String(ps.stored_location).trim());
    const activeAndShared = ps.active === true && ps.shared_stock === true;
    return locationOk && activeAndShared;
  });
}

/**
 * Build listing title from product: name + brand + model (space-separated, skip empty).
 * @param {Product | null} product
 * @returns {string}
 */
export function buildListingTitle(product) {
  if (!product) return '—';
  const parts = [
    product.name,
    product.brand,
    product.product_model
  ].filter((v) => v != null && String(v).trim() !== '');
  return parts.join(' ').trim() || '—';
}
